import { useQuery } from '@tanstack/react-query';
import { api_host, api_auth } from '../common/globals/constants';
import { getApiKeys } from '../utils/fns/get-api-keys';
import { format } from 'date-fns';

// ---------------------------------------------------------------------------
// Helper: construye los query-params comunes a todos los endpoints
// ---------------------------------------------------------------------------
const buildParams = (user, token, filters) => {
  const params = new URLSearchParams({
    user,
    key: api_auth,
    token,
  });

  if (filters?.selectedApp && filters.selectedApp !== '') {
    params.append('app', filters.selectedApp);
  }
  if (filters?.selectedPlatform && filters.selectedPlatform !== '') {
    params.append('store', filters.selectedPlatform);
  }
  if (filters?.selectedVersion && filters.selectedVersion !== '') {
    params.append('version', filters.selectedVersion);
  }

  if (filters?.selectedPeriod) {
    const time = filters.selectedPeriod.period || 'w';
    params.append('t', time);

    if (time === 'c' && filters.selectedPeriod.statePeriod) {
      params.append('fechaInicio', format(new Date(filters.selectedPeriod.statePeriod.startDate), 'yyyy-MM-dd'));
      params.append('fechaTermino', format(new Date(filters.selectedPeriod.statePeriod.endDate), 'yyyy-MM-dd'));
    }
  } else {
    params.append('t', 'w');
  }

  return params;
};

// ---------------------------------------------------------------------------
// Fetcher genérico
// ---------------------------------------------------------------------------
const fetchEndpoint = async (endpoint, user, token, filters) => {
  const params = buildParams(user, token, filters);
  const url = `${api_host}/reporte/online/indicadores/${endpoint}/?${params.toString()}`;

  const response = await fetch(url);
  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}`);
    error.status = response.status;
    throw error;
  }
  return response.json();
};

// ---------------------------------------------------------------------------
// Hook base reutilizable
// ---------------------------------------------------------------------------
const useSentimientoEndpoint = (queryKey, endpoint, filters, options = {}) => {
  const { user, token } = getApiKeys();

  return useQuery({
    queryKey: [queryKey, user, filters],
    queryFn: () => fetchEndpoint(endpoint, user, token, filters),
    enabled: !!user && !!token,
    staleTime: 1000 * 60 * 5, // 5m
    ...options,
  });
};

// ---------------------------------------------------------------------------
// Hooks públicos — uno por endpoint
// ---------------------------------------------------------------------------

/** Métricas resumen (totalReviews, avgSentiment, positivePercent, negativePercent) */
export const useSentimientoSummary = (filters, options = {}) =>
  useSentimientoEndpoint('sentimientoSummary', 'getSentimientoSummary', filters, options);

/** Tendencia mensual (categories, positive, negative, neutral) */
export const useSentimientoTrend = (filters, options = {}) =>
  useSentimientoEndpoint('sentimientoTrend', 'getSentimientoTrend', filters, options);

/** Temas críticos / fricciones (categories, totals) */
export const useSentimientoFriction = (filters, options = {}) =>
  useSentimientoEndpoint('sentimientoFriction', 'getSentimientoFriction', filters, options);

/** Sentimiento por versión / releases (versions, positive, neutral, negative) */
export const useSentimientoReleases = (filters, options = {}) =>
  useSentimientoEndpoint('sentimientoReleases', 'getSentimientoReleases', filters, options);

/** Nube de palabras (array de {text, value}) */
export const useSentimientoWordcloud = (filters, options = {}) =>
  useSentimientoEndpoint('sentimientoWordcloud', 'getSentimientoWordcloud', filters, options);
